/**
 * KnowledgeGraph — singleton store mirroring the designStore pattern.
 *
 * Holds:
 *   - nodes:  Map<id, KGNode>
 *   - edges:  Map<key, KGEdge>
 *   - file content cache: Map<filePath, sourceText>   (used during cascade)
 *
 * Updates are pushed via:
 *   onFileChanged(path, content)   — index/re-index a single file
 *   onFileRemoved(path)            — drop a file + cascade
 *   onSpecChanged(spec)            — index/re-index a design spec
 *   onSpecRemoved(specId)          — drop a spec + cascade
 *
 * Each entry-point internally batches via `runUpdate(...)` which:
 *   1. computes a set of "dirty" node ids
 *   2. processes them BFS, capping at MAX_CASCADE_ITERATIONS to prevent
 *      runaway in case of cycles
 *   3. emits exactly one subscriber callback at the end
 *
 * Subscribers see a fully-converged snapshot — never a half-updated one.
 */

import { extractFile, resolveImportPath } from './extractor';
import { edgeKey, looksLikeTestPath, pathSide } from './graphHelpers';
import type { ApiSide, KGAssetCompositeRole, KGAssetInput, KGEdge, KGNode, KGRunbookInput, KGSnapshot, KGSpecInput } from './types';

const MAX_CASCADE_ITERATIONS = 200;

interface Store {
  nodes: Map<string, KGNode>;
  edges: Map<string, KGEdge>;
  fileContent: Map<string, string>;
  /** id → set of edge keys that ORIGINATE from this node (for fast deletion). */
  outEdgesByNode: Map<string, Set<string>>;
  /** id → set of edge keys that POINT AT this node (for fast cascade). */
  inEdgesByNode: Map<string, Set<string>>;
  version: number;
  lastUpdatedAt: string;
}

const store: Store = {
  nodes: new Map(),
  edges: new Map(),
  fileContent: new Map(),
  outEdgesByNode: new Map(),
  inEdgesByNode: new Map(),
  version: 0,
  lastUpdatedAt: new Date(0).toISOString(),
};

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Memoised snapshot — useSyncExternalStore needs `getSnapshot` to
 * return a stable reference between notifications, otherwise React
 * sees a new object every render and bails with "getSnapshot should
 * be cached to avoid an infinite loop". We rebuild this object only
 * inside notify(); reads in between get the same instance back.
 */
let cachedSnapshot: KGSnapshot = computeSnapshot();

function notify(): void {
  store.version++;
  store.lastUpdatedAt = new Date().toISOString();
  cachedSnapshot = computeSnapshot();
  for (const fn of listeners) {
    try {
      fn();
    } catch (err) {
      console.error('[KG] listener error:', err);
    }
  }
}

function computeSnapshot(): KGSnapshot {
  const rawNodes = [...store.nodes.values()];
  const nodes: KGNode[] = [];
  for (const n of rawNodes) {
    if (n.kind === 'api') {
      const inKeys = store.inEdgesByNode.get(n.id);
      const hasEdges = !!inKeys && inKeys.size > 0;
      const provided = (n.providedSides?.length ?? 0) > 0;
      if (!hasEdges && !provided) continue; // orphan api node — hide
      nodes.push({ ...n, apiSides: [...definedSidesForApi(n.id)].sort() });
    } else {
      nodes.push(n);
    }
  }
  const edges = [...store.edges.values()];
  return {
    nodes,
    edges,
    version: store.version,
    lastUpdatedAt: store.lastUpdatedAt,
    stats: {
      files:    nodes.filter(n => n.kind === 'file').length,
      tests:    nodes.filter(n => n.kind === 'test').length,
      symbols:  nodes.filter(n => n.kind === 'symbol').length,
      specs:    nodes.filter(n => n.kind === 'spec').length,
      runbooks: nodes.filter(n => n.kind === 'runbook').length,
      assets:   nodes.filter(n => n.kind === 'asset').length,
      apis:     nodes.filter(n => n.kind === 'api').length,
      edges:    edges.length,
      danglingEdges: edges.filter(e => e.status === 'dangling').length,
    },
  };
}

// ── Public read API ─────────────────────────────────────────────────────

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getSnapshot(): KGSnapshot {
  return cachedSnapshot;
}

export function getNode(id: string): KGNode | undefined {
  return store.nodes.get(id);
}

/**
 * Recursive walk over outgoing edges starting at `rootId`, depth-capped.
 * Returns the set of visited node ids (excluding the root). Used by the
 * UI's "related to" panel for any node and by future LLM lookups.
 *
 * Cycles are handled by the visited-set short-circuit; "chain reaches
 * back to itself" terminates the walk.
 */
export function findRelatedNodes(rootId: string, maxDepth = 4): string[] {
  const visited = new Set<string>([rootId]);
  const queue: Array<{ id: string; depth: number }> = [{ id: rootId, depth: 0 }];
  const order: string[] = [];
  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (depth >= maxDepth) continue;
    const outKeys = store.outEdgesByNode.get(id);
    if (!outKeys) continue;
    for (const k of outKeys) {
      const e = store.edges.get(k);
      if (!e) continue;
      if (visited.has(e.target)) continue;
      visited.add(e.target);
      order.push(e.target);
      queue.push({ id: e.target, depth: depth + 1 });
    }
    // Also traverse INCOMING edges so the panel shows "who depends on me".
    const inKeys = store.inEdgesByNode.get(id);
    if (!inKeys) continue;
    for (const k of inKeys) {
      const e = store.edges.get(k);
      if (!e) continue;
      if (visited.has(e.source)) continue;
      visited.add(e.source);
      order.push(e.source);
      queue.push({ id: e.source, depth: depth + 1 });
    }
  }
  return order;
}

// ── Asset query helpers (thin reads over the adjacency indexes) ─────────

/**
 * 'What code uses this asset' — sources of incoming file-uses-asset edges,
 * with each edge's resolution status. Pure read over inEdgesByNode.
 */
export function filesUsingAsset(assetPath: string): Array<{ file: string; status: 'resolved' | 'dangling' }> {
  const id = `asset:${assetPath}`;
  const out: Array<{ file: string; status: 'resolved' | 'dangling' }> = [];
  const inKeys = store.inEdgesByNode.get(id);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e && e.kind === 'file-uses-asset') out.push({ file: e.source, status: e.status });
  }
  return out;
}

/**
 * 'What assets does this file/system need' — paths of outgoing
 * file-uses-asset targets. With `expandGroups` (default true) each asset is
 * expanded through its composite group so e.g. an animation sheet pulls in its
 * sibling frames json. Pure read over outEdgesByNode.
 */
export function assetsUsedByFile(filePath: string, opts?: { expandGroups?: boolean }): string[] {
  const expandGroups = opts?.expandGroups ?? true;
  const result = new Set<string>();
  const outKeys = store.outEdgesByNode.get(filePath);
  if (outKeys) for (const k of outKeys) {
    const e = store.edges.get(k);
    if (!e || e.kind !== 'file-uses-asset') continue;
    const assetNode = store.nodes.get(e.target);
    const path = assetNode?.label ?? e.target.replace(/^asset:/, '');
    result.add(path);
    if (expandGroups && assetNode?.assetGroup) {
      const groupId = `${ASSET_GROUP_PREFIX}${assetNode.assetGroup}`;
      const members = store.inEdgesByNode.get(groupId);
      if (members) for (const mk of members) {
        const me = store.edges.get(mk);
        if (!me || me.kind !== 'asset-part-of-group') continue;
        const sibling = store.nodes.get(me.source);
        if (sibling) result.add(sibling.label);
      }
    }
  }
  return [...result];
}

/**
 * Swap-impact for an asset: the files that reference it, plus the specs
 * covering those files.
 *
 * Specs are derived two ways and unioned:
 *   1. Direct spec-describes-asset edges into the asset (pipeline linkage).
 *   2. Specs that describe a SYMBOL whose file is one of the referencing files
 *      (spec-describes-symbol → symbol → filePath). This is an approximation:
 *      the graph has no direct spec-covers-file edge, so we bridge through the
 *      symbol nodes a spec already points at.
 */
export function assetImpact(assetPath: string): { files: string[]; specs: string[] } {
  const id = `asset:${assetPath}`;
  const files = new Set<string>();
  const inKeys = store.inEdgesByNode.get(id);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e && e.kind === 'file-uses-asset') files.add(e.source);
  }

  const specs = new Set<string>();
  // (1) Direct spec linkage on the asset.
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e && e.kind === 'spec-describes-asset') specs.add(e.source);
  }
  // (2) Specs whose described symbol lives in a referencing file.
  for (const file of files) {
    for (const [nodeId, n] of store.nodes) {
      if (n.kind !== 'symbol' || n.filePath !== file) continue;
      const sIn = store.inEdgesByNode.get(nodeId);
      if (sIn) for (const k of sIn) {
        const e = store.edges.get(k);
        if (e && e.kind === 'spec-describes-symbol') specs.add(e.source);
      }
    }
  }
  return { files: [...files], specs: [...specs] };
}

// ── Internal edge bookkeeping ───────────────────────────────────────────

function addEdge(e: Omit<KGEdge, 'key' | 'lastUpdatedAt'>): void {
  const key = edgeKey(e.kind, e.source, e.target);
  const now = new Date().toISOString();
  store.edges.set(key, { ...e, key, lastUpdatedAt: now });
  if (!store.outEdgesByNode.has(e.source)) store.outEdgesByNode.set(e.source, new Set());
  store.outEdgesByNode.get(e.source)!.add(key);
  if (!store.inEdgesByNode.has(e.target)) store.inEdgesByNode.set(e.target, new Set());
  store.inEdgesByNode.get(e.target)!.add(key);
}

function removeEdge(key: string): void {
  const e = store.edges.get(key);
  if (!e) return;
  store.edges.delete(key);
  store.outEdgesByNode.get(e.source)?.delete(key);
  store.inEdgesByNode.get(e.target)?.delete(key);
}

/** Drop ALL outgoing edges from a node (used when re-indexing it). */
function clearOutEdges(nodeId: string): Set<string> {
  const dropped = new Set<string>();
  const keys = store.outEdgesByNode.get(nodeId);
  if (!keys) return dropped;
  for (const k of [...keys]) {
    const e = store.edges.get(k);
    if (e) dropped.add(e.target);
    removeEdge(k);
  }
  return dropped;
}

function upsertNode(node: Omit<KGNode, 'lastIndexedAt'>): void {
  const existing = store.nodes.get(node.id);
  store.nodes.set(node.id, {
    ...(existing ?? {}),
    ...node,
    lastIndexedAt: new Date().toISOString(),
  });
}

/** All sides a $api member is defined on — incoming file-defines-api edges plus framework-provided sides. */
function definedSidesForApi(apiId: string): Set<ApiSide> {
  const sides = new Set<ApiSide>();
  const node = store.nodes.get(apiId);
  for (const s of node?.providedSides ?? []) sides.add(s);
  const inKeys = store.inEdgesByNode.get(apiId);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e && e.kind === 'file-defines-api' && e.apiSide) sides.add(e.apiSide);
  }
  return sides;
}

/** Single source of truth for edge resolution. Side-strict for file-uses-api. */
function computeEdgeStatus(e: KGEdge): KGEdge['status'] {
  if (e.kind === 'file-uses-api') {
    if (!store.nodes.has(e.target)) return 'dangling';
    const defined = definedSidesForApi(e.target);
    const useSide = e.apiSide ?? 'shared';
    return (defined.has(useSide) || defined.has('shared')) ? 'resolved' : 'dangling';
  }
  return store.nodes.has(e.target) && store.nodes.has(e.source) ? 'resolved' : 'dangling';
}

// ── Indexing primitives (return set of "affected" nodeIds to cascade) ──

function indexFileInternal(path: string, content: string): Set<string> {
  const affected = new Set<string>();
  store.fileContent.set(path, content);
  const isTest = looksLikeTestPath(path);
  upsertNode({
    id: path,
    kind: isTest ? 'test' : 'file',
    label: path,
    filePath: path,
  });

  const previousTargets = clearOutEdges(path);
  for (const t of previousTargets) affected.add(t);

  const { imports, exports, apiDefines, apiUses, assetUses, registrySide } = extractFile(content);
  const fileSide = pathSide(path);
  const defineSide: ApiSide = registrySide ?? fileSide;
  const knownFiles = new Set(store.fileContent.keys());

  for (const imp of imports) {
    const resolved = resolveImportPath(path, imp.source, knownFiles);
    if (resolved) {
      // file-imports-file (or test-covers-file when source is a test)
      const kind = isTest ? 'test-covers-file' as const : 'file-imports-file' as const;
      addEdge({ kind, source: path, target: resolved, status: 'resolved' });
      affected.add(resolved);
    } else {
      // External / bare specifier — record as dangling so the UI can
      // show it but it doesn't poison "broken" counts for resolvable
      // project imports.
      const externalId = `external:${imp.source}`;
      if (!store.nodes.has(externalId)) {
        upsertNode({ id: externalId, kind: 'file', label: imp.source });
      }
      addEdge({
        kind: 'file-imports-file',
        source: path,
        target: externalId,
        status: 'dangling',
      });
    }
  }

  for (const exp of exports) {
    const symbolId = `${path}#${exp.name}`;
    upsertNode({
      id: symbolId,
      kind: 'symbol',
      label: exp.name,
      filePath: path,
      symbolName: exp.name,
    });
    addEdge({
      kind: 'file-exports-symbol',
      source: path,
      target: symbolId,
      status: 'resolved',
    });
    affected.add(symbolId);
  }

  for (const name of apiDefines) {
    const apiId = `api:${name}`;
    upsertNode({ id: apiId, kind: 'api', label: name });
    addEdge({ kind: 'file-defines-api', source: path, target: apiId, status: 'resolved', apiSide: defineSide });
    affected.add(apiId);
  }
  for (const name of apiUses) {
    const apiId = `api:${name}`;
    const useEdge: KGEdge = { key: '', lastUpdatedAt: '', kind: 'file-uses-api', source: path, target: apiId, status: 'dangling', apiSide: fileSide };
    addEdge({ kind: 'file-uses-api', source: path, target: apiId, status: computeEdgeStatus(useEdge), apiSide: fileSide });
    affected.add(apiId);
  }
  for (const assetPath of assetUses) {
    const assetId = `asset:${assetPath}`;
    addEdge({ kind: 'file-uses-asset', source: path, target: assetId, status: store.nodes.has(assetId) ? 'resolved' : 'dangling' });
    affected.add(assetId);
  }

  // Resolution check: any pre-existing edge pointing AT this file or its
  // symbols may now resolve differently. Cascade adds them as affected.
  const inKeys = store.inEdgesByNode.get(path);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e) affected.add(e.source);
  }

  return affected;
}

function indexSpecInternal(spec: KGSpecInput): Set<string> {
  const affected = new Set<string>();
  const id = spec.id;
  upsertNode({
    id,
    kind: 'spec',
    label: spec.title,
    specId: id,
  });

  const previousTargets = clearOutEdges(id);
  for (const t of previousTargets) affected.add(t);

  // spec-describes-symbol edges.
  // Don't create placeholder symbol nodes here — the cascade uses
  // node existence as the resolution signal, and placeholders would
  // make every dangling edge look resolved. The symbol gets a real
  // node when its file is indexed (via file-exports-symbol).
  for (const ref of spec.relatedCodeSymbols ?? []) {
    const symbolId = ref.symbol ? `${ref.path}#${ref.symbol}` : ref.path;
    addEdge({
      kind: 'spec-describes-symbol',
      source: id,
      target: symbolId,
      status: store.nodes.has(symbolId) ? 'resolved' : 'dangling',
    });
    affected.add(symbolId);
  }

  // spec-references-spec edges
  for (const otherId of spec.relatedSpecIds ?? []) {
    addEdge({
      kind: 'spec-references-spec',
      source: id,
      target: otherId,
      status: store.nodes.has(otherId) ? 'resolved' : 'dangling',
    });
    affected.add(otherId);
  }

  // spec-describes-asset edges. These are DECLARED by the asset (which carries
  // its specId), but the edge originates from the spec — so clearOutEdges above
  // wiped it. Rebuild from any asset node that names this spec so the link
  // survives spec re-index.
  for (const [nodeId, n] of store.nodes) {
    if (n.kind === 'asset' && n.assetSpecId === id) {
      addEdge({ kind: 'spec-describes-asset', source: id, target: nodeId, status: 'resolved' });
      affected.add(nodeId);
    }
  }

  return affected;
}

function removeFileInternal(path: string): Set<string> {
  const affected = new Set<string>();
  // Outgoing edges
  const outKeys = store.outEdgesByNode.get(path);
  if (outKeys) for (const k of [...outKeys]) {
    const e = store.edges.get(k);
    if (e) affected.add(e.target);
    removeEdge(k);
  }
  // Incoming edges → mark dangling instead of removing (other nodes
  // still know they USED to reference this file).
  const inKeys = store.inEdgesByNode.get(path);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e) {
      e.status = 'dangling';
      affected.add(e.source);
    }
  }
  // Symbol nodes under this file go away.
  for (const [id, n] of store.nodes) {
    if (n.filePath === path && n.kind === 'symbol') {
      const sOut = store.outEdgesByNode.get(id);
      if (sOut) for (const k of [...sOut]) removeEdge(k);
      const sIn = store.inEdgesByNode.get(id);
      if (sIn) for (const k of sIn) {
        const e = store.edges.get(k);
        if (e) {
          e.status = 'dangling';
          affected.add(e.source);
        }
      }
      store.nodes.delete(id);
    }
  }
  store.nodes.delete(path);
  store.fileContent.delete(path);
  return affected;
}

function removeSpecInternal(specId: string): Set<string> {
  const affected = new Set<string>();
  const outKeys = store.outEdgesByNode.get(specId);
  if (outKeys) for (const k of [...outKeys]) {
    const e = store.edges.get(k);
    if (e) affected.add(e.target);
    removeEdge(k);
  }
  const inKeys = store.inEdgesByNode.get(specId);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (e) {
      e.status = 'dangling';
      affected.add(e.source);
    }
  }
  store.nodes.delete(specId);
  return affected;
}

// ── Public write API (these batch + notify) ─────────────────────────────

function runUpdate(initialAffected: Set<string>): void {
  // BFS over affected nodes, recomputing edges that touch them. For
  // file/spec nodes whose CONTENT changes, the caller has already
  // re-indexed them — the cascade is here to find downstream nodes
  // whose RESOLUTION status may have flipped (e.g. a now-existing
  // target that used to be dangling).
  const visited = new Set<string>();
  const queue = [...initialAffected];
  let iterations = 0;
  while (queue.length > 0 && iterations < MAX_CASCADE_ITERATIONS) {
    iterations++;
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);

    // Recompute "status" on every edge touching this node.
    const flipped = new Set<string>();
    const checkEdge = (e: KGEdge) => {
      const newStatus = computeEdgeStatus(e);
      if (newStatus !== e.status) {
        e.status = newStatus;
        flipped.add(e.source);
        flipped.add(e.target);
      }
    };
    const outKeys = store.outEdgesByNode.get(id);
    if (outKeys) for (const k of outKeys) {
      const e = store.edges.get(k);
      if (e) checkEdge(e);
    }
    const inKeys = store.inEdgesByNode.get(id);
    if (inKeys) for (const k of inKeys) {
      const e = store.edges.get(k);
      if (e) checkEdge(e);
    }
    for (const f of flipped) {
      if (!visited.has(f)) queue.push(f);
    }
  }
  if (iterations >= MAX_CASCADE_ITERATIONS) {
    console.warn('[KG] cascade hit max iterations — graph may be incomplete');
  }
  notify();
}

export function onFileChanged(path: string, content: string): void {
  const affected = indexFileInternal(path, content);
  runUpdate(affected);
}

export function onFileRemoved(path: string): void {
  const affected = removeFileInternal(path);
  runUpdate(affected);
}

export function onSpecChanged(spec: KGSpecInput): void {
  const affected = indexSpecInternal(spec);
  runUpdate(affected);
}

function indexRunbookInternal(runbook: KGRunbookInput): Set<string> {
  const affected = new Set<string>();
  const id = runbook.id;
  upsertNode({ id, kind: 'runbook', label: runbook.title, runbookId: id });

  const previousTargets = clearOutEdges(id);
  for (const t of previousTargets) affected.add(t);

  // runbook-addresses-symbol edges. Like specs, don't create placeholder
  // symbol nodes — node existence is the resolution signal; the symbol gets
  // a real node when its file is indexed (file-exports-symbol).
  for (const ref of runbook.relatedCodeSymbols ?? []) {
    const symbolId = ref.symbol ? `${ref.path}#${ref.symbol}` : ref.path;
    addEdge({
      kind: 'runbook-addresses-symbol',
      source: id,
      target: symbolId,
      status: store.nodes.has(symbolId) ? 'resolved' : 'dangling',
    });
    affected.add(symbolId);
  }

  // runbook-references-spec edges
  for (const otherId of runbook.relatedSpecIds ?? []) {
    addEdge({
      kind: 'runbook-references-spec',
      source: id,
      target: otherId,
      status: store.nodes.has(otherId) ? 'resolved' : 'dangling',
    });
    affected.add(otherId);
  }

  return affected;
}

export function onRunbookChanged(runbook: KGRunbookInput): void {
  const affected = indexRunbookInternal(runbook);
  runUpdate(affected);
}

const ASSET_GROUP_PREFIX = 'asset-group:';

/**
 * Derive a composite group's subtype from the roles of its members. A group
 * accrues members one onAssetChanged at a time, so we infer from whatever's
 * present so far: sheet+frames → animation, tileset-image+tilemap → tileset,
 * sfx/music → audio, sprite → sprite, data → data. With a single member known
 * we fall back to `group/<role>`.
 */
function deriveGroupSubtype(groupId: string): string {
  const roles = new Set<KGAssetCompositeRole>();
  const inKeys = store.inEdgesByNode.get(groupId);
  if (inKeys) for (const k of inKeys) {
    const e = store.edges.get(k);
    if (!e || e.kind !== 'asset-part-of-group') continue;
    const member = store.nodes.get(e.source);
    const role = member?.assetRole;
    if (role) roles.add(role);
  }
  let kind: string;
  if (roles.has('sheet') || roles.has('frames')) kind = 'animation';
  else if (roles.has('tileset-image') || roles.has('tilemap')) kind = 'tileset';
  else if (roles.has('sfx') || roles.has('music')) kind = 'audio';
  else if (roles.has('sprite')) kind = 'sprite';
  else if (roles.has('data')) kind = 'data';
  else kind = [...roles][0] ?? 'unknown';
  return `group/${kind}`;
}

function indexAssetInternal(input: KGAssetInput): Set<string> {
  const affected = new Set<string>();
  const id = `asset:${input.path}`;

  // Replacement semantics: same path + unchanged hash is a no-op upsert (don't
  // churn). Same path + DIFFERENT hash is an in-place swap — we re-upsert (which
  // bumps lastIndexedAt) but DO NOT touch incoming file-uses-asset edges: the
  // path is the contract, not the hash, so referencing code stays valid.
  const existing = store.nodes.get(id);
  if (existing && input.hash !== undefined && existing.assetHash === input.hash
      && existing.assetGroup === input.composite?.group) {
    // No content change and no group change — nothing to re-index.
    return affected;
  }

  // Re-index rebuilds the asset's composite/spec linkage from `input`, so drop
  // any prior such edges first — otherwise a group move (A→B) or specId change
  // leaves the stale edge live and orphans the old group node. Capture vacated
  // groups to garbage-collect below. The membership edge is an OUT-edge
  // (asset→group); spec-describes-asset is an IN-edge (spec→asset). Incoming
  // file-uses-asset edges are deliberately untouched — the path is the contract.
  const vacatedGroups: string[] = [];
  const priorOut = store.outEdgesByNode.get(id);
  if (priorOut) for (const k of [...priorOut]) {
    const e = store.edges.get(k);
    if (e?.kind === 'asset-part-of-group') { vacatedGroups.push(e.target); removeEdge(k); }
  }
  const priorIn = store.inEdgesByNode.get(id);
  if (priorIn) for (const k of [...priorIn]) {
    const e = store.edges.get(k);
    if (e?.kind === 'spec-describes-asset') { affected.add(e.source); removeEdge(k); }
  }

  upsertNode({
    id, kind: 'asset', label: input.path, assetSubtype: input.subtype,
    assetHash: input.hash,
    assetGroup: input.composite?.group,
    assetRole: input.composite?.role,
    assetSpecId: input.specId,
  });
  affected.add(id);

  // Composite group node + membership edge.
  if (input.composite) {
    const groupId = `${ASSET_GROUP_PREFIX}${input.composite.group}`;
    addEdge({ kind: 'asset-part-of-group', source: id, target: groupId, status: 'resolved' });
    // Upsert the group node AFTER the edge so deriveGroupSubtype sees this member.
    upsertNode({ id: groupId, kind: 'asset', label: input.composite.group, assetSubtype: deriveGroupSubtype(groupId) });
    affected.add(groupId);
  }

  // Spec → asset linkage. resolved iff the spec node already exists, else
  // dangling — mirroring spec-describes-symbol's existence-based status.
  if (input.specId) {
    addEdge({
      kind: 'spec-describes-asset', source: input.specId, target: id,
      status: store.nodes.has(input.specId) ? 'resolved' : 'dangling',
    });
    affected.add(input.specId);
  }

  // GC any group the asset just left (skip the one it's still in).
  const keptGroup = input.composite ? `${ASSET_GROUP_PREFIX}${input.composite.group}` : null;
  for (const groupId of vacatedGroups) {
    if (groupId !== keptGroup) cleanupEmptyGroup(groupId, affected);
  }

  // Incoming file-uses-asset (and any other in-edges) re-resolve against this
  // node now existing / re-indexed — land their sources in `affected` so
  // swap-impact subscribers fire.
  const inKeys = store.inEdgesByNode.get(id);
  if (inKeys) for (const k of inKeys) { const e = store.edges.get(k); if (e) affected.add(e.source); }
  return affected;
}

/**
 * After a group loses a member, either delete the group node (no members left)
 * or re-derive its subtype from the survivors. Shared by asset removal and the
 * group-move path in indexAssetInternal. Adds `groupId` to `affected`.
 */
function cleanupEmptyGroup(groupId: string, affected: Set<string>): void {
  affected.add(groupId);
  const remaining = store.inEdgesByNode.get(groupId);
  const memberCount = remaining
    ? [...remaining].filter(k => store.edges.get(k)?.kind === 'asset-part-of-group').length
    : 0;
  if (memberCount === 0) {
    // No members left — drop the group node and any edges still touching it.
    const gOut = store.outEdgesByNode.get(groupId);
    if (gOut) for (const k of [...gOut]) removeEdge(k);
    const gIn = store.inEdgesByNode.get(groupId);
    if (gIn) for (const k of [...gIn]) removeEdge(k);
    store.nodes.delete(groupId);
  } else {
    upsertNode({ id: groupId, kind: 'asset', label: store.nodes.get(groupId)?.label ?? groupId, assetSubtype: deriveGroupSubtype(groupId) });
  }
}

function removeAssetInternal(path: string): Set<string> {
  const affected = new Set<string>();
  const id = `asset:${path}`;

  // Capture the group(s) this asset belongs to before we drop its out-edges so
  // we can delete a now-empty group node afterward.
  const groupIds: string[] = [];
  const outKeys = store.outEdgesByNode.get(id);
  if (outKeys) for (const k of [...outKeys]) {
    const e = store.edges.get(k);
    if (e?.kind === 'asset-part-of-group') groupIds.push(e.target);
    // Drop ALL out-edges of the asset (group membership, etc.); the asset node
    // is going away so nothing should still claim it as a source.
    if (e) { affected.add(e.target); removeEdge(k); }
  }

  // Incoming edges (file-uses-asset, spec-describes-asset) → mark dangling, not
  // removed: the referencing file/spec still knows it USED to point here.
  const inKeys = store.inEdgesByNode.get(id);
  if (inKeys) for (const k of inKeys) { const e = store.edges.get(k); if (e) { e.status = 'dangling'; affected.add(e.source); } }
  store.nodes.delete(id);

  // Delete any group node whose last member just disappeared.
  for (const groupId of groupIds) cleanupEmptyGroup(groupId, affected);
  return affected;
}

export function onAssetChanged(input: KGAssetInput): void {
  const affected = indexAssetInternal(input);
  // No-op upsert (same path + unchanged hash + unchanged group) yields an empty
  // affected set — skip notify() so an unchanged re-sync doesn't churn the
  // snapshot version. Every real change adds at least the asset id.
  if (affected.size === 0) return;
  runUpdate(affected);
}

export function onAssetRemoved(path: string): void {
  runUpdate(removeAssetInternal(path));
}

/** Register a framework-injected $api member (e.g. client canvas/ctx) so uses don't falsely dangle. */
export function onApiProvided(name: string, side: ApiSide): void {
  const id = `api:${name}`;
  const existing = store.nodes.get(id);
  const set = new Set<ApiSide>(existing?.providedSides ?? []);
  set.add(side);
  upsertNode({ id, kind: 'api', label: name, providedSides: [...set] });
  runUpdate(new Set([id]));
}

export function onSpecRemoved(specId: string): void {
  const affected = removeSpecInternal(specId);
  runUpdate(affected);
}

/** Test-only: dump everything. */
export function _reset(): void {
  store.nodes.clear();
  store.edges.clear();
  store.fileContent.clear();
  store.outEdgesByNode.clear();
  store.inEdgesByNode.clear();
  store.version = 0;
  store.lastUpdatedAt = new Date(0).toISOString();
  cachedSnapshot = computeSnapshot();
  // Don't notify on reset — tests call subscribe AFTER reset.
}
